// File: client/src/components/CardHand.js
// Interactive component rendering the player's card hand.

import React from 'react';
import { Sparkles, HelpCircle, ArrowRight } from 'lucide-react';

const CardHand = ({
  hand = [],
  activeEffects = [],
  turnState,
  isActiveTurn,
  onPlayCard,
  playerColor,
}) => {
  const getRarityStyles = (rarity) => {
    switch (rarity) {
      case 'rare':
        return {
          bg: 'bg-gradient-to-br from-fuchsia-600 via-pink-600 to-amber-500',
          border: 'border-amber-400 shadow-fuchsia-500/20',
          text: 'text-amber-200',
          badge: 'bg-amber-500/30 text-amber-200 border-amber-500/50',
        };
      case 'uncommon':
        return {
          bg: 'bg-gradient-to-br from-purple-600 via-violet-700 to-indigo-800',
          border: 'border-purple-400 shadow-purple-500/20',
          text: 'text-purple-200',
          badge: 'bg-purple-500/30 text-purple-200 border-purple-500/50',
        };
      case 'common':
      default:
        return {
          bg: 'bg-gradient-to-br from-slate-700 via-slate-800 to-slate-900',
          border: 'border-slate-600 shadow-black/40',
          text: 'text-slate-300',
          badge: 'bg-slate-500/20 text-slate-300 border-slate-500/30',
        };
    }
  };

  const isPlayable = isActiveTurn && turnState && !turnState.cardPlayed;

  return (
    <div className="w-full max-w-4xl mt-6 px-4">
      {/* Hand Header */}
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-2">
          <span className="text-lg font-bold text-white flex items-center gap-2">
            🃏 Your Hand ({playerColor === 'white' ? '⚪ White' : '⚫ Black'})
          </span>
          {!isPlayable && isActiveTurn && (
            <span className="text-xs px-2 py-0.5 bg-yellow-500/20 text-yellow-400 rounded-full border border-yellow-500/30 font-medium">
              Card already played this turn
            </span>
          )}
          {!isActiveTurn && (
            <span className="text-xs px-2 py-0.5 bg-slate-500/20 text-slate-400 rounded-full border border-slate-500/30 font-medium">
              Waiting for turn...
            </span>
          )}
        </div>
        <span className="text-xs text-slate-400">
          Max hand: 5 cards • Draws 1 card at the end of each turn
        </span>
      </div>

      {/* Cards List */}
      {hand.length === 0 ? (
        <div className="flex flex-col items-center justify-center py-6 px-4 bg-slate-800/40 rounded-xl border border-slate-700/50">
          <HelpCircle size={32} className="text-slate-500 mb-2" />
          <p className="text-slate-400 text-sm">Your hand is empty.</p>
        </div>
      ) : (
        <div className="flex flex-wrap justify-center sm:justify-start gap-4">
          {hand.map((card) => {
            const styles = getRarityStyles(card.rarity);
            const canPlayThisCard = isPlayable;

            return (
              <div
                key={card.instance_id}
                onClick={() => canPlayThisCard && onPlayCard(card)}
                className={`relative w-44 h-60 rounded-xl p-4 flex flex-col justify-between border-2 shadow-lg transition-all duration-300 select-none ${
                  styles.bg
                } ${styles.border} ${
                  canPlayThisCard
                    ? 'hover:-translate-y-4 hover:shadow-2xl hover:scale-105 cursor-pointer border-opacity-100 hover:border-white'
                    : 'grayscale opacity-60 cursor-not-allowed'
                }`}
              >
                {/* Rarity & Card Type Badges */}
                <div className="flex justify-between items-center">
                  <span
                    className={`text-[10px] px-1.5 py-0.5 rounded-full border font-bold uppercase tracking-wider ${styles.badge}`}
                  >
                    {card.rarity}
                  </span>
                  <span className="text-[10px] text-white/60 font-semibold uppercase tracking-wider">
                    {card.card_type}
                  </span>
                </div>

                {/* Card Title & Content */}
                <div className="my-auto flex flex-col items-center text-center">
                  <span className="text-white font-bold text-lg tracking-tight mb-1 drop-shadow-md">
                    {card.name}
                  </span>
                  <p className="text-[11px] text-white/90 leading-snug font-medium line-clamp-4">
                    {card.description}
                  </p>
                </div>

                {/* Footer action hint */}
                <div className="flex justify-between items-center text-white/60 text-[9px] font-semibold mt-1">
                  <span className="flex items-center gap-1">
                    {card.duration_turns > 1 ? `⏳ ${card.duration_turns} Turns` : '⚡ Instant'}
                  </span>
                  {canPlayThisCard && (
                    <span className="flex items-center gap-0.5 text-white animate-pulse">
                      Play <ArrowRight size={10} />
                    </span>
                  )}
                </div>

                {/* Holographic sparkle overlay for Rares */}
                {card.rarity === 'rare' && (
                  <Sparkles
                    size={14}
                    className="absolute top-2 right-2 text-amber-300 animate-bounce"
                  />
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default CardHand;
